import React, { Suspense, lazy } from 'react'
import { AnonSessionRoute, AuthRoute, NotFound, Redirect, Route, Switch, useHistory } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { NoticeLoading } from '@fs/zion-icon'
import { RecordAuthRoute } from '@fs/zion-gedcomx'
import { LoadingSkeleton as RecordLoadingSkeleton } from '@fs/zion-record-details'
import Cookies from 'js-cookie'
import NavigationTabs from './components/hr/NavigationTabs'

/**
 * This will retry failed chunks up to 5 times
 * Hopefully it will decrease Sentry errors
 * @param {Function} lazyComponent - lazy component import function
 * @param {Number} attemptsLeft - number of attempts to make (defaults to 5)
 * @returns {Promise<any>} Promise that rejects if all additional attempts fail.
 */
function componentLoader(lazyComponent, attemptsLeft = 5) {
  return new Promise((resolve, reject) => {
    lazyComponent()
      .then(resolve)
      .catch((error) => {
        // let us retry after 1500 ms
        setTimeout(() => {
          if (attemptsLeft === 1) {
            reject(error)
            return
          }
          componentLoader(lazyComponent, attemptsLeft - 1).then(resolve, reject)
        }, 1500)
      })
  })
}

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy
const CatalogDetails = lazy(() => componentLoader(() => import('./components/prototypes/catalog/CatalogDetailsPage')))
const CollectionDetails = lazy(() => componentLoader(() => import('./components/hr/CollectionDetailsPage')))
const CollectionListPage = lazy(() => componentLoader(() => import('./components/hr/CollectionListPage')))
const FilmDebugPage = lazy(() => componentLoader(() => import('./components/prototypes/film-debug/FilmDebugPage')))
const GenCollectionDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenCollectionDetailsPage')))
const GenSearchPage = lazy(() => componentLoader(() => import('./components/gen/GenSearchPage')))
const GenResultsPage = lazy(() => componentLoader(() => import('./components/gen/GenResultsPage')))
const GenSubmissionsPage = lazy(() => componentLoader(() => import('./components/gen/GenSubmissionsPage')))
const GenSubmissionDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenSubmissionDetailsPage')))
const HRComparePage = lazy(() => componentLoader(() => import('./components/prototypes/hr/HRComparePage')))
const LocationListPage = lazy(() => componentLoader(() => import('./components/location/LocationListPage')))
const LocationPage = lazy(() => componentLoader(() => import('./components/location/LocationPage')))
const RecordDetailsPage = lazy(() => componentLoader(() => import('./components/hr/RecordDetailsPage')))
const SearchHomePage = lazy(() => componentLoader(() => import('./components/hr/SearchHomePage')))
const SearchResultsPage = lazy(() => componentLoader(() => import('./components/hr/SearchResultsPage')))
const SearchResultsLoadingSkeleton = lazy(() =>
  componentLoader(() => import('@fs/zion-record-search').then((module) => ({ default: module.LoadingSkeletons })))
)
const TreeSearchPage = lazy(() => componentLoader(() => import('./components/tree/TreeSearchPage')))
const TreeResultsPage = lazy(() => componentLoader(() => import('./components/tree/TreeResultsPage')))

const EMBEDDED_MOBILE_COOKIE_NAME = 'embeddedMobile'

function App() {
  // Hide NavigationTabs if page is embedded in mobile app WebView
  const embeddedMobile = !!Cookies.get(EMBEDDED_MOBILE_COOKIE_NAME)
  const query = useHistory()?.location?.search || ''

  return (
    <ErrorBoundary>
      <Suspense fallback={<NoticeLoading />}>
        {!embeddedMobile && <NavigationTabs />}

        <Switch>
          {/* Search Home */}
          <Route path="/" exact component={SearchHomePage} />

          {/* Record Details */}
          <RecordAuthRoute
            path="/ark\:/61903/1\:1\::arkPath"
            component={RecordDetailsPage}
            LoadingSkeleton={<RecordLoadingSkeleton />}
          />

          {/* These location redirects are required BEFORE the HR collections paths */}
          <Redirect from="/collection/location/:region" exact to={`/location/:region?${query}`} />
          <Redirect from="/collection/location/:region/:subRegion" exact to={`/location/:region/:subRegion?${query}`} />

          {/* HR Collections */}
          <Route path="/collection/list" component={CollectionListPage} />
          <Route path="/1940census" component={CollectionDetails} />
          <Route path="/collection/:collectionId" component={CollectionDetails} />

          {/* Locations */}
          <Route path="/location/list" exact component={LocationListPage} />
          <AnonSessionRoute path="/location/:region" exact component={LocationPage} />
          <AnonSessionRoute path="/location/:region/:subRegion" exact component={LocationPage} />

          {/* HR Search Results */}
          <RecordAuthRoute
            path="/record/results"
            component={SearchResultsPage}
            LoadingSkeleton={<SearchResultsLoadingSkeleton />}
          />

          {/* Tree Search & Results */}
          <AuthRoute path="/tree/results" component={TreeResultsPage} requirePersonalData />
          <Route exact path="/tree" component={() => <Redirect to="/tree/name" />} />
          <Route path="/tree/:searchType" exact component={TreeSearchPage} requirePersonalData />

          {/* Genealogies */}
          {<Route path="/genealogies" exact component={GenSearchPage} requirePersonalData />}
          <Route path="/genealogies/results" component={GenResultsPage} requirePersonalData />
          <Route
            path="/genealogies/collection/:collectionId"
            component={GenCollectionDetailsPage}
            requirePersonalData
          />
          <Route path="/genealogies/submissions" component={GenSubmissionsPage} requirePersonalData />
          <Route
            path="/genealogies/submission/:collectionId/:submissionId"
            component={GenSubmissionDetailsPage}
            requirePersonalData
          />

          {/* Redirects for genealogies pages */}
          <Redirect exact from="/family-trees" to={`/genealogies${query}`} />
          <Redirect from="/family-trees/results" to={`/genealogies/results${query}`} />

          {/* Old routes for genealogies pages */}
          {<Route path="/family-trees" exact component={GenSearchPage} requirePersonalData />}
          <Route path="/family-trees/results" component={GenResultsPage} requirePersonalData />

          {/* hidden routes; users shouldn't visit these */}
          <Route path="/catalog(/oclc)?/:catId" component={CatalogDetails} />
          <Route path="/filmdebug" component={FilmDebugPage} />
          <AuthRoute path="/hrcompare" component={HRComparePage} />
          <Route path="/blank" component={() => 'This page is intentionally left blank'} />

          {/* no route found */}
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
export default App
